'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  getProductAnalytics,
  publicSurface,
  type BlogSurface,
  type ProductAnalytics,
  type ProductAnalyticsAction,
} from '@/lib/product-analytics';

/** Public first-use navigation only; no recorder, DOM observer, or workspace instrumentation. */
export function ProductAnalyticsEvents({
  client,
  blogPage,
}: {
  client?: ProductAnalytics;
  blogPage?: { pathname: string; surface: BlogSurface };
}) {
  const pathname = usePathname();
  const blogPathname = blogPage?.pathname;
  const blogSurface = blogPage?.surface;
  useEffect(() => {
    // A valid server-rendered article/listing opts in. Unknown blog URLs and 404s
    // do not acquire collection merely because their path starts with /blog.
    const renderedBlog = pathname === blogPathname ? blogSurface : undefined;
    if (!pathname || (!renderedBlog && !publicSurface(pathname))) return;
    const analytics = client ?? getProductAnalytics();
    if (!analytics) return;
    const record = (action: ProductAnalyticsAction) =>
      renderedBlog
        ? analytics.recordBlog(renderedBlog, action, document.referrer)
        : analytics.record(pathname, action, document.referrer);
    void record('view');
    const onClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest('a[href]');
      if (!anchor) return;
      let target: URL;
      try {
        target = new URL(anchor.getAttribute('href')!, window.location.href);
      } catch {
        return;
      }
      if (
        target.origin !== window.location.origin &&
        !['https://sentrial.com', 'https://www.sentrial.com'].includes(target.origin)
      )
        return;
      const action =
        target.pathname === '/docs/quickstart'
          ? 'quickstart'
          : target.pathname === '/docs/installation'
            ? 'installation'
            : null;
      if (action) void record(action);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [pathname, client, blogPathname, blogSurface]);
  return null;
}
